import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { type Signal, signal } from "../lib/signal.js";
import {
  ensureRoom,
  isEmpty,
  snapshot,
  type WorkspaceDoc,
  workspaceRoot,
  type WorkspaceSnapshot,
} from "./schema.js";
import { defaultSyncUrl, SyncProvider } from "./sync.js";

export type Workspace = {
  id: string;
  doc: Y.Doc;
  root: WorkspaceDoc;
  /** A plain snapshot of the document. The shell never touches a `Y.Map`. */
  snapshot: Signal<WorkspaceSnapshot>;
  /** Null when sync is switched off; the shell is fully usable either way. */
  sync: SyncProvider | null;
  /** Where object documents sync, or false for a local-only workspace (§6.2). */
  syncUrl: string | false;
  destroy: () => void;
};

export type OpenOptions = {
  /** A relay URL, or `false` for a local-only workspace. */
  sync?: string | false;
};

/**
 * Open the workspace document and its IndexedDB store (§4.2, §4.3).
 *
 * Nothing renders until the local store has replayed. Bootstrapping the first
 * room before that would create a second "Home" on every reload.
 */
const BOOTSTRAP_WAIT_MS = 2500;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function openWorkspace(id: string, options: OpenOptions = {}): Promise<Workspace> {
  const doc = new Y.Doc({ guid: id });
  const persistence = new IndexeddbPersistence(`gelatos:${id}`, doc);
  await persistence.whenSynced;

  const root = workspaceRoot(doc);

  // Sync starts after the local store has replayed, so the first thing the relay
  // hears from this device is a delta rather than a whole document.
  const url = options.sync === undefined ? defaultSyncUrl() : options.sync;
  const sync =
    url === false ? null : new SyncProvider({ url, workspaceId: id, docId: "workspace", doc });

  // Bootstrapping the first room before hearing from the relay would give every
  // device that joins an existing workspace its own spurious "Home". So when
  // the local document is empty and a relay is configured, wait briefly for the
  // first batch. Offline is still fine: the wait has a deadline, and a device
  // that really is first creates the room itself.
  if (isEmpty(root) && sync !== null) {
    await Promise.race([sync.firstSync, delay(BOOTSTRAP_WAIT_MS)]);
  }
  ensureRoom(root, Date.now());

  const state = signal(snapshot(root));
  const observer = (): void => state.set(snapshot(root));
  root.rooms.observeDeep(observer);
  root.objects.observeDeep(observer);

  return {
    id,
    doc,
    root,
    snapshot: state,
    sync,
    syncUrl: url,
    destroy(): void {
      sync?.destroy();
      root.rooms.unobserveDeep(observer);
      root.objects.unobserveDeep(observer);
      void persistence.destroy();
      doc.destroy();
    },
  };
}
